/**
 * QUÉ SIGNIFICA QUE «¿QUIÉN SOY?» NO SALGA BIEN. La regla, aparte y sin React.
 *
 * DE DÓNDE SALE, Y ES UNA CLASE ENTERA MIRANDO. El 24 de septiembre de 2026, Gabri compartía
 * pantalla desde un Chromebook enseñando las lecciones del Cielo y **le apareció el formulario de
 * acceso delante de los alumnos**. Su sesión estaba bien —la cookie dura una semana—: lo que
 * falló fue la comprobación del arranque, y el código de entonces trataba *cualquier* fallo como
 * «aquí no hay nadie». Un 502 del servidor gratuito despertando, un parpadeo del wifi del
 * instituto o un tiempo agotado salían por la misma puerta que un 401 de verdad.
 *
 * LA DISTINCIÓN ES TODA LA PIEZA:
 *
 *   · **El servidor dice que no** (401 sin sesión, 403 sin permiso): eso es una respuesta, y
 *     entonces sí, a la pantalla de acceso.
 *   · **El servidor no dice nada** (no hay respuesta, o es un 5xx): de un silencio no se deduce
 *     que alguien no tenga sesión. Se reintenta, y si sigue callado se dice lo que pasa.
 *
 * Y NO ES UNA PUERTA: quien decide si hay sesión sigue siendo el servidor, en cada petición, con
 * la cookie firmada. Esto sólo decide QUÉ SE LE ENSEÑA A QUIEN ESTÁ DELANTE mientras tanto.
 *
 * Lo prueba `scripts/prueba_sesion.mjs`.
 */

export const COMPROBANDO = "comprobando";
export const DENTRO = "dentro";
export const FUERA = "fuera";
export const SIN_RESPUESTA = "sin-respuesta";
/** El servidor ha contestado, y lo que ha contestado es «vas demasiado rápido» (429). */
export const FRENO = "freno";

/**
 * CUÁNTAS VECES SE PREGUNTA Y CUÁNDO, en milisegundos desde la anterior.
 *
 * Cuatro intentos en unos diez segundos, y el número sale de lo que hay enfrente: el plan
 * gratuito de Render tarda cerca de un minuto en despertar, pero para eso ya está la pantalla de
 * reconexión, que reintenta sola y sin límite. Aquí lo que se cubre es el tropiezo corto —el
 * servidor lleno un segundo, el wifi del aula parpadeando— sin dejar a nadie diez segundos
 * mirando «Verificando sesión…» cuando de verdad no hay sesión.
 */
export const INTENTOS = [0, 1200, 3000, 6000];

/**
 * Qué se deduce de un fallo al preguntar quién eres.
 *
 * EL TERCER CASO ES DEL 6 DE OCTUBRE DE 2026, y hasta ese día salía por la puerta del silencio.
 * Un 429 no es un silencio: es la respuesta más clara que da un servidor. Dice «estás pidiendo
 * demasiado» y dice cuántos segundos faltan. Tratarlo como «no ha contestado» hacía que esta
 * comprobación reintentara cuatro veces en diez segundos, que es exactamente lo único que
 * garantiza otro 429 —y lo que convirtió un freno de un minuto en un bucle que no se acababa:
 * cada vuelta gastaba del límite que estaba esperando a que se vaciara—.
 *
 * (La razón de que el navegador no viera siquiera el 429 era otra, y está en `backend/server.py`,
 * donde el CORS se montaba por dentro del freno. Las dos cosas tenían que estar mal a la vez.)
 *
 * @param {object} error  el error de axios: puede traer `response.status` o no traer nada.
 * @returns {"fuera"|"freno"|"sin-respuesta"}
 */
export function loQueDice(error) {
  const dice = error?.response?.status;
  if (dice === 401 || dice === 403) return FUERA;
  if (dice === 429) return FRENO;
  return SIN_RESPUESTA;
}

/**
 * Cuántos segundos pide esperar el servidor, de su propia cabecera `Retry-After`.
 *
 * No se inventa ninguno si no viene: cero significa «no lo ha dicho», y quien lo enseñe decide
 * qué hacer con eso. Escribir aquí un número por si acaso sería una segunda fuente para un dato
 * que el servidor ya manda, y el día que cambie el límite esta mentiría.
 */
export function segundosDeFreno(error) {
  const dice = Number(error?.response?.headers?.["retry-after"]);
  return Number.isFinite(dice) && dice > 0 ? Math.ceil(dice) : 0;
}

export default {
  COMPROBANDO, DENTRO, FRENO, FUERA, INTENTOS, SIN_RESPUESTA, loQueDice, segundosDeFreno,
};
